import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Transition } from 'react-transition-group'

import UIIcon from '@/components/UI/UIIcon'
import { stateValueToString, useAppState } from '@/composables/useAppState'
import { useRunnerUITransition } from '@/composables/useRunnerUITransition'
import useTracking from '@/composables/useTracking'
import AudioEngine from '@/webgl/audio/AudioEngine'

import ToggleButton from './ToggleButton'

export default function SoundButton() {
  const { t } = useTranslation()
  const { state, persona, scenarioUnitName } = useAppState()
  const { trackClick } = useTracking()
  const [mute, setMute] = useState(false)

  const buttonRef = useRef<HTMLLabelElement>(null)
  const { onEnter, onExit, timeout } = useRunnerUITransition(buttonRef, { from: 'bottom' })

  const isVisible = !state.matches('idle') && !state.matches('loading')
  const setChecked = (value: boolean) => {
    trackClick({
      clickType: 'button',
      unitName: state.matches('intro')
        ? `${persona}_simulation${scenarioUnitName}`
        : `${stateValueToString(state.value)}_${persona}_simulation${scenarioUnitName}`,
      unitVariation: state.matches('intro') ? `intro_meet_child` : stateValueToString(state.value),
      unitLocation: 'tyce',
      buttonText: 'mute',
    })
    setMute(value)
    AudioEngine.setMute(value)
  }

  return (
    <Transition
      in={isVisible}
      onEnter={onEnter}
      onExit={onExit}
      unmountOnExit
      mountOnEnter
      timeout={timeout}
      nodeRef={buttonRef}
    >
      <ToggleButton
        ref={buttonRef}
        checked={mute}
        setChecked={setChecked}
        theme='text-scarlet'
        label={t('global.mute')}
      >
        <div
          className='absolute size-full inset-0 flex justify-center items-center'
          title={mute ? t('global.unmute') : t('global.mute')}
        >
          {!mute && (
            <UIIcon
              className='ToggleButton__Icon text-16 relative group-hover:scale-95 transition-transform duration-333 ease-out-quad text-dark-blue fill-current'
              icon='soundOn'
            />
          )}
          {mute && (
            <UIIcon
              className='ToggleButton__Icon text-16 relative group-hover:scale-95 transition-transform duration-333 ease-out-quad text-dark-blue fill-current'
              icon='soundOff'
            />
          )}
        </div>
      </ToggleButton>
    </Transition>
  )
}
